/**
 * Lightweight share helper. Uses the native Web Share API where available
 * (mobile Safari, Android Chrome, modern desktop), gracefully falls back to
 * clipboard copy + a toast so the user always gets a working action.
 *
 * Returns: { method: "native" | "clipboard" | "failed", error?: Error }
 */

const safeNavigator = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return null;
  return navigator;
};

export const isNativeShareAvailable = (data) => {
  const nav = safeNavigator();
  if (!nav || typeof nav.share !== "function") return false;
  // Some browsers expose `canShare` — when present, respect it.
  if (typeof nav.canShare === "function") {
    try { return nav.canShare(data); } catch { return false; }
  }
  return true;
};

export const copyToClipboard = async (text) => {
  const nav = safeNavigator();
  try {
    if (nav?.clipboard?.writeText) {
      await nav.clipboard.writeText(text);
      return true;
    }
    // Legacy fallback for old browsers / unsecured contexts
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

export const shareOrCopy = async ({ title, text, url }) => {
  const data = { title, text, url };
  if (isNativeShareAvailable(data)) {
    try {
      await navigator.share(data);
      return { method: "native" };
    } catch (e) {
      // User cancelled or share failed — fall through to clipboard fallback.
      if (e?.name === "AbortError") return { method: "cancelled" };
    }
  }
  const payload = url ? `${text}\n${url}` : text;
  const ok = await copyToClipboard(payload);
  return { method: ok ? "clipboard" : "failed" };
};

export const buildWhatsAppUrl = (text) =>
  `https://wa.me/?text=${encodeURIComponent(text)}`;

export const buildMailtoUrl = ({ subject, body }) =>
  `mailto:?subject=${encodeURIComponent(subject || "")}&body=${encodeURIComponent(body || "")}`;
